<template>
  <section class="admin-page">
    <div v-if="isLoading" class="loading-overlay" role="status" aria-live="polite">
      <div class="loading-box">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p>Consultando registros. Aguarde alguns instantes...</p>
      </div>
    </div>

    <div class="admin-shell">
      <header class="admin-header">
        <div>
          <p class="eyebrow">Painel administrativo</p>
          <h1>Administração de agendamentos</h1>
          <p class="header-copy">
            Consulte solicitações por período, acompanhe pendências e atualize o status dos agendamentos.
          </p>
        </div>

        <div class="header-actions">
          <button type="button" class="btn btn-outline-primary" @click="gerarRelatorioCSV">
            Gerar CSV
          </button>
        </div>
      </header>

      <section class="filters-panel" aria-label="Filtros de agendamentos">
        <div class="filter-grid">
          <label class="filter-field" for="dataInicio">
            <span>Data inicial</span>
            <input id="dataInicio" type="date" v-model="dataInicio" />
          </label>

          <label class="filter-field" for="dataFim">
            <span>Data final</span>
            <input id="dataFim" type="date" v-model="dataFim" />
          </label>

          <label class="filter-field" for="filtroCnpj">
            <span>CNPJ</span>
            <input
              id="filtroCnpj"
              type="text"
              placeholder="Digite o CNPJ"
              v-model="tempFiltroCnpj"
              @keyup.enter="aplicarFiltros"
            />
          </label>

          <label class="filter-field" for="filtroEmail">
            <span>E-mail</span>
            <input
              id="filtroEmail"
              type="text"
              placeholder="responsavel@email.com"
              v-model="tempFiltroEmail"
              @keyup.enter="aplicarFiltros"
            />
          </label>

          <label class="filter-field" for="filtroTicket">
            <span>Ticket</span>
            <input
              id="filtroTicket"
              type="text"
              placeholder="Número do ticket"
              v-model="tempFiltroTicket"
              @keyup.enter="aplicarFiltros"
            />
          </label>
        </div>

        <div class="filter-actions">
          <button type="button" class="btn btn-primary" @click="buscarComFiltros">
            Buscar
          </button>
          <button type="button" class="btn btn-outline-secondary" @click="limparFiltros">
            Limpar
          </button>
          <button
            type="button"
            class="btn btn-outline-warning"
            :class="{ active: exibirElementos }"
            @click="toggleExibicao"
          >
            {{ exibirElementos ? 'Mostrando duplicados' : 'Somente duplicados' }}
          </button>
        </div>
      </section>

      <section class="summary-grid" aria-label="Resumo por status">
        <button
          v-for="item in statusOptions"
          :key="item.value"
          type="button"
          class="summary-card"
          :class="[{ active: filtroStatus === item.value }, `summary-${item.tone}`]"
          @click="filtroStatus = item.value"
        >
          <span>{{ item.label }}</span>
          <strong>{{ item.total }}</strong>
        </button>
      </section>

      <div class="results-toolbar">
        <div>
          <strong>{{ registrosExibidos.length }}</strong>
          <span>{{ registrosExibidos.length === 1 ? 'registro encontrado' : 'registros encontrados' }}</span>
        </div>
        <p v-if="periodoSelecionado">{{ periodoSelecionado }}</p>
      </div>

      <section v-if="registrosExibidos.length" class="records-list">
        <article
          v-for="(registro, index) in registrosExibidos"
          :key="registroTicket(registro) || registro.id || index"
          class="record-card"
          :class="{ duplicated: registroDuplicado(registro.id), agency: isAgencia(registro) }"
        >
          <div class="record-main">
            <div class="record-title-group">
              <div class="record-title-line">
                <h2>{{ valorCampo(registro.instituicao) }}</h2>
                <span class="status-badge" :class="statusClass(statusRegistro(registro))">
                  {{ statusLabel(statusRegistro(registro)) }}
                </span>
              </div>

              <div class="record-meta">
                <span>Ticket {{ valorCampo(registroTicket(registro)) }}</span>
                <span>Código {{ valorCampo(registro.id) }}</span>
                <span>Entrada {{ formatarData(registro.logsregistro) }}</span>
              </div>
            </div>

            <div v-if="registroDuplicado(registro.id)" class="duplicate-alert">
              Possível duplicidade: há mais de uma solicitação ativa com este código.
            </div>

            <div class="route-highlight">
              <span>Roteiro, horário e dia</span>
              <strong>{{ extrairoteiro(registro.idbruto || registro.id) }}</strong>
            </div>

            <dl class="info-grid">
              <div>
                <dt>Responsável</dt>
                <dd>{{ valorCampo(registro.email) }}</dd>
              </div>
              <div>
                <dt>CNPJ</dt>
                <dd>{{ valorCampo(registro.cnpj) }}</dd>
              </div>
              <div>
                <dt>Visitantes</dt>
                <dd>{{ valorCampo(registro.quantidade) }}</dd>
              </div>
              <div>
                <dt>Contato</dt>
                <dd>{{ valorCampo(registro.nomecontato) }}</dd>
              </div>
            </dl>

            <div v-if="isAgencia(registro)" class="agency-block">
              <p>Agência responsável</p>
              <dl>
                <div>
                  <dt>Nome</dt>
                  <dd>{{ valorCampo(registro.instituicao2) }}</dd>
                </div>
                <div>
                  <dt>CNPJ</dt>
                  <dd>{{ valorCampo(registro.cnpj2) }}</dd>
                </div>
                <div>
                  <dt>E-mail</dt>
                  <dd>{{ valorCampo(registro.obs3) }}</dd>
                </div>
                <div>
                  <dt>Telefone</dt>
                  <dd>{{ valorCampo(registro.obs2) }}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div class="record-side">
            <div class="record-actions" :atributo="eadminsera">
              <button
                v-if="eadminsera"
                type="button"
                class="btn btn-success"
                @click="aprovar(registroTicket(registro), 1, registro.email)"
              >
                Aprovar
              </button>
              <button
                v-if="eadminsera"
                type="button"
                class="btn btn-secondary"
                @click="aprovar(registroTicket(registro), 2, registro.email)"
              >
                Reservar
              </button>
              <button
                v-if="eadminsera"
                type="button"
                class="btn btn-danger"
                @click="aprovar(registroTicket(registro), 0, registro.email)"
              >
                Cancelar
              </button>
            </div>

          </div>
            <div class="accordion details-accordion" :id="`accordionAdmin${index}`">
              <div class="accordion-item">
                <h3 class="accordion-header" :id="`headingAdmin${index}`">
                  <button
                    class="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    :data-bs-target="`#collapseAdmin${index}`"
                    aria-expanded="false"
                    :aria-controls="`collapseAdmin${index}`"
                  >
                    Detalhes
                  </button>
                </h3>
                <div
                  :id="`collapseAdmin${index}`"
                  class="accordion-collapse collapse"
                  :aria-labelledby="`headingAdmin${index}`"
                  :data-bs-parent="`#accordionAdmin${index}`"
                >
                  <div class="accordion-body">
                    <dl class="details-grid">
                      <div>
                        <dt>Endereço</dt>
                        <dd>{{ valorCampo(registro.endereco) }}</dd>
                      </div>
                      <div>
                        <dt>Faixa etária/Série</dt>
                        <dd>{{ valorCampo(registro.series) }}</dd>
                      </div>
                      <div>
                        <dt>Tipo da instituição</dt>
                        <dd>{{ valorCampo(registro.tipo) }}</dd>
                      </div>
                      <div>
                        <dt>Deficiência/necessidades</dt>
                        <dd>{{ formatarDeficiencia(registro.deficiencia) }}</dd>
                      </div>
                      <div>
                        <dt>Cargo</dt>
                        <dd>{{ valorCampo(registro.cargo) }}</dd>
                      </div>
                      <div>
                        <dt>WhatsApp</dt>
                        <dd>{{ valorCampo(registro.obswp) }}</dd>
                      </div>
                      <div class="details-wide">
                        <dt>Informações de contato</dt>
                        <dd>{{ valorCampo(registro.obs) }}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
        </article>
      </section>

      <section v-else class="empty-state">
        <h2>Nenhum registro encontrado</h2>
        <p>Revise o período, os filtros aplicados ou selecione outro status.</p>
      </section>
    </div>
  </section>
</template>

<script>
import { computed, ref, onMounted, watch } from "vue";
import axios from "axios";

import { getCurrentUser } from "@/services/authService";
import { isMasterFull } from "@/utils/auth";

export default {
  setup() {
    const hojeInput = () => {
      const hoje = new Date();
      const ano = hoje.getFullYear();
      const mes = String(hoje.getMonth() + 1).padStart(2, "0");
      const dia = String(hoje.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    };

    const dataInicio = ref(hojeInput());
    const dataFim = ref(hojeInput());
    const filtroCnpj = ref("");
    const filtroEmail = ref("");
    const filtroTicket = ref("");
    const tempFiltroCnpj = ref("");
    const tempFiltroEmail = ref("");
    const tempFiltroTicket = ref("");
    const administrador = ref(false);
    const filtroStatus = ref("todos");
    const registros = ref([]);
    const isLoading = ref(false);
    const exibirElementos = ref(false);
    const requisicaoAtual = ref(0);

    const emails = getCurrentUser();
    const eadminsera = isMasterFull();

    const valorCampo = (valor) => {
      if (Array.isArray(valor)) {
        return valor.length ? valor.join(", ") : "Informações não preenchidas";
      }

      return valor ? valor : "Informações não preenchidas";
    };

    const registroTicket = (registro) => registro.bid || registro.ticket || "";

    const statusRegistro = (registro) => registro.estadoaprovacao ?? registro.aprovacao ?? "pendente";

    const formatarDeficiencia = (deficiencia) => {
      if (!deficiencia || (Array.isArray(deficiencia) && !deficiencia.length)) {
        return "Informações não preenchidas";
      }

      if (!Array.isArray(deficiencia)) {
        return valorCampo(deficiencia);
      }

      return deficiencia
        .map((item) => {
          if (!item || typeof item !== "object") {
            return valorCampo(item);
          }

          const partes = [];

          if (item.tipo) {
            partes.push(item.tipo);
          }

          if (item.quantidade) {
            partes.push(`${item.quantidade} ${Number(item.quantidade) === 1 ? "pessoa" : "pessoas"}`);
          }

          if (item.descricao) {
            partes.push(item.descricao);
          }

          return partes.join(" - ") || "Informações não preenchidas";
        })
        .join("; ");
    };

    const statusLabel = (status) => {
      const labels = {
        0: "Cancelado",
        1: "Confirmado",
        2: "Reservado",
        4: "Cancelamento solicitado",
        pendente: "Pendente",
      };

      return labels[status] || "Pendente";
    };

    const statusClass = (status) => {
      const classes = {
        0: "status-cancelado",
        1: "status-confirmado",
        2: "status-reservado",
        4: "status-cancelamento",
        pendente: "status-pendente",
      };

      return classes[status] || "status-pendente";
    };

    const isAgencia = (registro) =>
      registro.agencia === "É uma agência" ||
      registro.agencia === "Ã‰ uma agÃªncia" ||
      Boolean(registro.cnpj2);

    const aplicarFiltro = (tipo) => {
      if (tipo === "cnpj") {
        filtroCnpj.value = tempFiltroCnpj.value.trim();
      } else if (tipo === "email") {
        filtroEmail.value = tempFiltroEmail.value.trim();
      } else if (tipo === "ticket") {
        filtroTicket.value = tempFiltroTicket.value.trim();
      } else {
        alert("Nenhum registro com esses parâmetros foi encontrado.");
      }
    };

    const aplicarFiltros = () => {
      aplicarFiltro("cnpj");
      aplicarFiltro("email");
      aplicarFiltro("ticket");
    };

    const buscarComFiltros = () => {
      aplicarFiltros();
      buscar();
    };

    const limparCampoFiltro = (tipo) => {
      if (tipo === "cnpj") {
        tempFiltroCnpj.value = "";
        filtroCnpj.value = "";
      } else if (tipo === "email") {
        tempFiltroEmail.value = "";
        filtroEmail.value = "";
      } else if (tipo === "ticket") {
        tempFiltroTicket.value = "";
        filtroTicket.value = "";
      }
    };

    const criarDataLocal = (dataISO) => {
      if (!dataISO) return null;

      const [ano, mes, dia] = dataISO.split("-").map(Number);
      if (!ano || !mes || !dia) return null;

      return new Date(ano, mes - 1, dia);
    };

    const formatarDataInput = (data) => {
      const ano = data.getFullYear();
      const mes = String(data.getMonth() + 1).padStart(2, "0");
      const dia = String(data.getDate()).padStart(2, "0");
      return `${ano}-${mes}-${dia}`;
    };

    const criarDataRegistro = (valor) => {
      const texto = String(valor ?? "");
      const dataInput = texto.match(/\d{4}-\d{2}-\d{2}/)?.[0];

      if (dataInput) {
        return criarDataLocal(dataInput);
      }

      const data = new Date(texto);
      return Number.isNaN(data.getTime()) ? null : data;
    };

    const datasDoPeriodo = computed(() => {
      const inicio = criarDataLocal(dataInicio.value);
      const fim = criarDataLocal(dataFim.value);

      if (!inicio || !fim || inicio > fim) return [];

      const datas = [];
      const cursor = new Date(inicio);

      while (cursor <= fim) {
        datas.push(formatarDataInput(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }

      return datas;
    });

    const formatarData = (dataISO) => {
      if (!dataISO) return "Informações não preenchidas";

      const data = new Date(dataISO);
      if (Number.isNaN(data.getTime())) return dataISO;

      const opcoesData = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      };
      const opcoesHora = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      };

      return `${data.toLocaleDateString("pt-BR", opcoesData)} ${data.toLocaleTimeString("pt-BR", opcoesHora)}`;
    };

    const formatarDataCurta = (dataISO) => {
      if (!dataISO) return "";

      const [ano, mes, dia] = dataISO.split("-");
      if (!ano || !mes || !dia) return dataISO;

      return `${dia}/${mes}/${ano}`;
    };

    const periodoSelecionado = computed(() => {
      if (!dataInicio.value && !dataFim.value) return "";
      if (dataInicio.value && dataFim.value) {
        return `Período selecionado: ${formatarDataCurta(dataInicio.value)} a ${formatarDataCurta(dataFim.value)}`;
      }

      return dataInicio.value
        ? `A partir de ${formatarDataCurta(dataInicio.value)}`
        : `Até ${formatarDataCurta(dataFim.value)}`;
    });

    const registrosFiltradosPorPeriodo = computed(() => {
      const inicio = criarDataLocal(dataInicio.value);
      const fim = criarDataLocal(dataFim.value);

      if (!inicio && !fim) return registros.value;

      if (fim) {
        fim.setHours(23, 59, 59, 999);
      }

      return registros.value.filter((registro) => {
        const registroData = criarDataRegistro(registro.diamaster);

        if (!registroData) return false;
        if (inicio && registroData < inicio) return false;
        if (fim && registroData > fim) return false;

        return true;
      });
    });

    const normalizarTexto = (valor) => String(valor ?? "").toLowerCase().trim();
    const somenteDigitos = (valor) => String(valor ?? "").replace(/\D/g, "");

    const registrosBaseFiltrados = computed(() => {
      let filtrados = registrosFiltradosPorPeriodo.value;

      if (filtroCnpj.value) {
        const cnpjFormatado = somenteDigitos(filtroCnpj.value);
        filtrados = filtrados.filter((registro) =>
          somenteDigitos(registro.cnpj).includes(cnpjFormatado)
        );
      }

      if (filtroEmail.value) {
        filtrados = filtrados.filter((registro) =>
          normalizarTexto(registro.email).includes(normalizarTexto(filtroEmail.value))
        );
      }

      if (filtroTicket.value) {
        filtrados = filtrados.filter((registro) =>
          normalizarTexto(registroTicket(registro)).includes(normalizarTexto(filtroTicket.value))
        );
      }

      return filtrados;
    });

    const registrosFiltrados = computed(() => {
      return registrosBaseFiltrados.value.filter((registro) => {
        switch (filtroStatus.value) {
          case "pendente":
            return statusRegistro(registro) === "pendente";
          case "confirmado":
            return statusRegistro(registro) === "1";
          case "cancelado":
            return statusRegistro(registro) === "0";
          case "cancelamento solicitado":
            return statusRegistro(registro) === "4";
          case "reservado":
            return statusRegistro(registro) === "2";
          default:
            return true;
        }
      });
    });

    const registroDuplicado = (id) => {
      const registrosFiltradosValidos = registrosFiltrados.value.filter(
        (registro) => statusRegistro(registro) !== "4" && statusRegistro(registro) !== "0"
      );
      const count = registrosFiltradosValidos.filter((registro) => registro.id === id).length;
      return count > 1;
    };

    const registrosExibidos = computed(() => {
      if (!exibirElementos.value) {
        return registrosFiltrados.value;
      }

      return registrosFiltrados.value.filter((registro) => registroDuplicado(registro.id));
    });

    const totalPendentes = computed(
      () => registrosBaseFiltrados.value.filter((r) => statusRegistro(r) === "pendente").length
    );
    const totalConfirmados = computed(
      () => registrosBaseFiltrados.value.filter((r) => statusRegistro(r) === "1").length
    );
    const totalCancelados = computed(
      () => registrosBaseFiltrados.value.filter((r) => statusRegistro(r) === "0").length
    );
    const totalCancelamentoSolicitados = computed(
      () => registrosBaseFiltrados.value.filter((r) => statusRegistro(r) === "4").length
    );
    const totalReservados = computed(
      () => registrosBaseFiltrados.value.filter((r) => statusRegistro(r) === "2").length
    );
    const totalTodos = computed(() => registrosBaseFiltrados.value.length);

    const statusOptions = computed(() => [
      { value: "todos", label: "Todos", total: totalTodos.value, tone: "todos" },
      { value: "pendente", label: "Pendentes", total: totalPendentes.value, tone: "pendente" },
      { value: "confirmado", label: "Confirmados", total: totalConfirmados.value, tone: "confirmado" },
      { value: "reservado", label: "Reservados", total: totalReservados.value, tone: "reservado" },
      { value: "cancelado", label: "Cancelados", total: totalCancelados.value, tone: "cancelado" },
      {
        value: "cancelamento solicitado",
        label: "Cancelamentos solicitados",
        total: totalCancelamentoSolicitados.value,
        tone: "cancelamento",
      },
    ]);

    const extrairoteiro = (ide) => {
      const texto = String(ide ?? "");
      const regex = /(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/;
      const match = texto.match(regex);

      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10);
        const day = parseInt(match[3], 10);
        const date = new Date(year, month - 1, day);
        const dayOfWeek = date.getDay();

        const daysOfWeek = [
          "Domingo",
          "Segunda-feira",
          "Terça-feira",
          "Quarta-feira",
          "Quinta-feira",
          "Sexta-feira",
          "Sábado",
        ];
        const dayName = daysOfWeek[dayOfWeek];

        const isJuly2024 = year === 2024 && month === 7;
        const isWeekend = dayOfWeek === 6 || dayOfWeek === 0;

        if (match[7] === "14" || isJuly2024 || isWeekend || texto.endsWith("f")) {
          return texto.replace(regex, `Roteiro Livre - posição: $7 | $4h$5 | $3/$2/$1 (${dayName})`);
        }

        return texto.replace(regex, `Roteiro $7 | $4h$5 | $3/$2/$1 (${dayName})`);
      }

      return texto || "Informações não preenchidas";
    };

    const buscar = (silencioso = false) => {
      if (!dataInicio.value || !dataFim.value) {
        if (!silencioso) {
          alert("Informe a data inicial e a data final para consultar o período.");
        }
        return;
      }

      if (!datasDoPeriodo.value.length) {
        if (!silencioso) {
          alert("A data final deve ser igual ou posterior à data inicial.");
        }
        return;
      }

      carregarRegistros(emails, datasDoPeriodo.value);
    };

    const carregarRegistros = (param, diasSelecionados) => {
      const requisicaoId = requisicaoAtual.value + 1;
      requisicaoAtual.value = requisicaoId;
      isLoading.value = true;

      const consultas = diasSelecionados.map((diaselec) =>
        axios.post("https://museucatavento.org.br/sistema-agendamento/api2.php", {
          email: param,
          diaselecionado: diaselec,
          admin: eadminsera,
        })
      );

      Promise.all(consultas)
        .then((response) => {
          if (requisicaoId !== requisicaoAtual.value) return;

          const registrosEncontrados = response
            .flatMap((item) => (Array.isArray(item.data) ? item.data : []))
            .filter((item) => item != null);

          if (registrosEncontrados.length > 0) {
            registros.value = registrosEncontrados;
          } else {
            registros.value = [];
            alert("Não foram encontrados registros para este período.");
          }
        })
        .catch((error) => {
          if (requisicaoId !== requisicaoAtual.value) return;

          console.error(error);
          alert("Houve algum erro, por favor, tente novamente em instantes.");
        })
        .finally(() => {
          if (requisicaoId === requisicaoAtual.value) {
            isLoading.value = false;
          }
        });
    };

    const aprovar = (identifica, parametro, emaild) => {
      axios
        .post("https://museucatavento.org.br/sistema-agendamento/api4.php", {
          identificador: identifica,
          aprovacao: parametro,
        })
        .then((response) => {
          if (response.data == "1") {
            const registroAtualizado = registros.value.find((r) => registroTicket(r) === identifica);
            if (registroAtualizado) {
              registroAtualizado.estadoaprovacao = parametro.toString();
              registroAtualizado.aprovacao = parametro.toString();
            }
            alert("Status atualizado. Aguarde alguns segundos para visualizar a confirmação.");
            enviaraprovacao(emaild, identifica, parametro);
          } else {
            alert("Houve algum problema com sua solicitação.");
          }
        })
        .catch((error) => {
          console.error(error);
          alert("Houve algum erro, por favor, tente novamente em instantes.");
        });
    };

    const enviaraprovacao = (emaild, identifica, parametro) => {
      const registro = registros.value.find((r) => registroTicket(r) === identifica);

      if (!registro) {
        alert("Registro não encontrado.");
        return;
      }

      const dadosRegistro = {
        seuemail: emaild,
        bid: identifica,
        parametro,
        instituicao: registro.instituicao || "Informações não preenchidas",
        cnpj: registro.cnpj || "Informações não preenchidas",
        diamaster: registro.diamaster || "Informações não preenchidas",
        estadoaprovacao: statusRegistro(registro),
        email: registro.email || "Informações não preenchidas",
        endereco: registro.endereco || "Informações não preenchidas",
        quantidade: registro.quantidade || "Informações não preenchidas",
        series: registro.series || "Informações não preenchidas",
        tipo: registro.tipo || "Informações não preenchidas",
        deficiencia: formatarDeficiencia(registro.deficiencia),
        nomecontato: registro.nomecontato || "Informações não preenchidas",
        cargo: registro.cargo || "Informações não preenchidas",
        obs: registro.obs || "Informações não preenchidas",
        obswp: registro.obswp || "Informações não preenchidas",
      };

      axios
        .get("https://museucatavento.org.br/envio/envioaprovacao.php", {
          params: dadosRegistro,
        })
        .then(() => {
          alert("Enviado para o usuário.");
        })
        .catch((error) => {
          console.error("Erro ao enviar e-mail:", error);
        });
    };

    const escaparCSV = (valor) => `"${String(valor ?? "Informações não preenchidas").replace(/"/g, '""')}"`;

    const gerarRelatorioCSV = () => {
      const cabecalho = [
        "Nome da Instituição",
        "CNPJ",
        "ID",
        "Diamaster",
        "Status",
        "E-mail do Responsável",
        "Endereço",
        "Visitantes",
        "Faixa Etária/Série",
        "Tipo da Instituição",
        "Detalhes sobre PCD",
        "Nome do Contato",
        "Cargo",
        "Informações de Contato",
        "Whatsapp",
      ];

      const linhas = registrosFiltrados.value.map((registro) =>
        [
          registro.instituicao,
          registro.cnpj,
          registro.id,
          registro.diamaster,
          statusLabel(statusRegistro(registro)),
          registro.email,
          registro.endereco,
          registro.quantidade,
          registro.series,
          registro.tipo,
          formatarDeficiencia(registro.deficiencia),
          registro.nomecontato,
          registro.cargo,
          registro.obs,
          registro.obswp,
        ]
          .map(escaparCSV)
          .join(",")
      );

      const dadosCSV = [cabecalho.map(escaparCSV).join(","), ...linhas].join("\n");
      const blob = new Blob([dadosCSV], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "relatorio_reservas.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const toggleExibicao = () => {
      exibirElementos.value = !exibirElementos.value;
    };

    const showAlert = () => {
      alert("Estamos recuperando todas as informações. Por favor, aguarde...");
    };

    const limparFiltros = () => {
      tempFiltroCnpj.value = "";
      tempFiltroEmail.value = "";
      tempFiltroTicket.value = "";
      filtroCnpj.value = "";
      filtroEmail.value = "";
      filtroTicket.value = "";
      filtroStatus.value = "todos";
      exibirElementos.value = false;
      dataInicio.value = hojeInput();
      dataFim.value = hojeInput();
    };

    onMounted(() => {
      buscar();
      administrador.value = emails == "admin.agendamento@cataventomaster.com";
    });

    watch([dataInicio, dataFim], () => {
      buscar(true);
    });

    return {
      administrador,
      aplicarFiltro,
      aplicarFiltros,
      aprovar,
      buscar,
      buscarComFiltros,
      dataFim,
      dataInicio,
      eadminsera,
      emails,
      exibirElementos,
      extrairoteiro,
      filtroCnpj,
      filtroEmail,
      filtroStatus,
      filtroTicket,
      formatarDeficiencia,
      formatarData,
      formatarDataCurta,
      gerarRelatorioCSV,
      isAgencia,
      isLoading,
      limparCampoFiltro,
      limparFiltros,
      periodoSelecionado,
      registros,
      registrosExibidos,
      registrosFiltrados,
      registroDuplicado,
      registroTicket,
      showAlert,
      statusClass,
      statusLabel,
      statusOptions,
      statusRegistro,
      tempFiltroCnpj,
      tempFiltroEmail,
      tempFiltroTicket,
      toggleExibicao,
      totalCancelados,
      totalCancelamentoSolicitados,
      totalConfirmados,
      totalPendentes,
      totalReservados,
      totalTodos,
      valorCampo,
    };
  },
};
</script>

<style lang="scss" scoped>
.admin-page {
  background: #f5f7fa;
  min-height: 100vh;
  padding: clamp(1.25rem, 3vw, 2.5rem) 0 3rem;
  text-align: left;
}

.admin-shell {
  width: min(1440px, calc(100% - 2rem));
  margin: 0 auto;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;
}

.eyebrow {
  color: var(--catavento-orange);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.admin-header h1 {
  margin: 0;
}

.header-copy {
  color: var(--catavento-muted);
  max-width: 680px;
  margin: 0.6rem 0 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filters-panel,
.empty-state {
  background: var(--catavento-surface);
  border: 1px solid var(--catavento-border);
  border-radius: var(--catavento-radius);
  box-shadow: 0 8px 22px rgba(20, 30, 45, 0.08);
}

.filters-panel {
  padding: 1rem;
  margin-bottom: 1rem;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(150px, 0.75fr)) repeat(3, minmax(180px, 1fr));
  gap: 0.85rem;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  font-weight: 800;
}

.filter-field span {
  color: var(--catavento-text);
}

.filter-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-top: 0.9rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-card {
  background: #fff;
  border: 1px solid var(--catavento-border);
  border-left: 5px solid #98a2b3;
  border-radius: var(--catavento-radius);
  color: var(--catavento-text);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  min-height: 82px;
  padding: 0.85rem;
  text-align: left;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.summary-card:hover,
.summary-card.active {
  border-color: var(--catavento-blue);
  box-shadow: 0 10px 24px rgba(20, 30, 45, 0.12);
  transform: translateY(-1px);
}

.summary-card span {
  color: var(--catavento-muted);
  font-size: 0.78rem;
  font-weight: 800;
}

.summary-card strong {
  font-size: 1.7rem;
  line-height: 1;
}

.summary-confirmado {
  border-left-color: #1f8a5b;
}

.summary-pendente {
  border-left-color: #c77700;
}

.summary-reservado {
  border-left-color: #667085;
}

.summary-cancelado {
  border-left-color: #c93434;
}

.summary-cancelamento {
  border-left-color: #b7791f;
}

.summary-todos {
  border-left-color: var(--catavento-blue);
}

.results-toolbar {
  align-items: center;
  color: var(--catavento-muted);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin: 1rem 0;
}

.results-toolbar strong {
  color: var(--catavento-text);
  font-size: 1.2rem;
}

.results-toolbar p {
  margin: 0;
}

.records-list {
  display: grid;
  gap: 1rem;
}

.record-card {
  background: #fff;
  border: 1px solid var(--catavento-border);
  border-left: 5px solid transparent;
  border-radius: var(--catavento-radius);
  box-shadow: 0 8px 22px rgba(20, 30, 45, 0.08);
  display: grid;
  grid-template-columns: minmax(0, 1fr) 230px;
  gap: 1rem;
  padding: 1rem;
}

.record-card.duplicated {
  border-left-color: #d28a17;
}

.record-card.agency {
  background: linear-gradient(90deg, rgba(22, 90, 150, 0.06), #fff 36%);
}

.record-title-line {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.record-title-line h2 {
  color: var(--catavento-text);
  font-size: 1.15rem;
  font-weight: 800;
  margin: 0;
}

.record-meta {
  color: var(--catavento-muted);
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.86rem;
  margin-top: 0.45rem;
}

.status-badge {
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 0.25rem 0.65rem;
  font-size: 0.78rem;
  font-weight: 800;
  white-space: nowrap;
}

.status-confirmado {
  background: #e6f4ed;
  color: #176847;
}

.status-pendente {
  background: #fff4db;
  color: #8a5200;
}

.status-cancelado {
  background: #fde8e8;
  color: #9b1c1c;
}

.status-reservado {
  background: #eef1f5;
  color: #475467;
}

.status-cancelamento {
  background: #fff8d7;
  color: #7a4b00;
}

.duplicate-alert {
  background: #fff8e6;
  border: 1px solid #f2c36b;
  border-radius: 6px;
  color: #7a4b00;
  font-weight: 700;
  margin-top: 0.85rem;
  padding: 0.65rem 0.75rem;
}

.route-highlight {
  background: #eff6ff;
  border: 1px solid #c9ddf3;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.85rem;
  padding: 0.75rem;
}

.route-highlight span,
dt {
  color: var(--catavento-muted);
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.route-highlight strong {
  color: var(--catavento-blue);
  line-height: 1.35;
}

.info-grid,
.details-grid,
.agency-block dl {
  display: grid;
  gap: 0.75rem;
  margin: 0;
}

.info-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-top: 0.95rem;
}

.details-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.agency-block dl {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

dt,
dd {
  margin: 0;
}

dd {
  color: var(--catavento-text);
  overflow-wrap: anywhere;
}

.agency-block {
  border-top: 1px solid var(--catavento-border);
  margin-top: 1rem;
  padding-top: 1rem;
}

.agency-block p {
  color: var(--catavento-blue);
  font-weight: 800;
  margin: 0 0 0.75rem;
}

.record-side {
  border-left: 1px solid var(--catavento-border);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-left: 1rem;
}

.record-actions {
  display: grid;
  gap: 0.6rem;
}

.details-accordion .accordion-item {
  border: 1px solid var(--catavento-border);
  border-radius: 6px;
  overflow: hidden;
}

.details-accordion .accordion-button {
  background: #f8fafc;
  color: var(--catavento-text);
  font-weight: 800;
  min-height: 42px;
}

.details-accordion .accordion-body {
  background: #fff;
}

.details-wide {
  grid-column: 1 / -1;
}

.empty-state {
  padding: 2rem;
  text-align: center;
}

.empty-state h2 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: var(--catavento-muted);
  margin: 0;
}

.loading-overlay {
  align-items: center;
  background: rgba(17, 24, 39, 0.55);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1200;
}

.loading-box {
  align-items: center;
  background: #fff;
  border-radius: var(--catavento-radius);
  box-shadow: var(--catavento-shadow);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  text-align: center;
  width: min(360px, 100%);
}

.loading-box p {
  margin: 0;
}

@media (max-width: 1200px) {
  .summary-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .record-card {
    grid-template-columns: 1fr;
  }

  .record-side {
    border-left: none;
    border-top: 1px solid var(--catavento-border);
    padding-left: 0;
    padding-top: 1rem;
  }

  .record-actions {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .admin-shell {
    width: min(100% - 1rem, 1440px);
  }

  .admin-header,
  .results-toolbar,
  .record-title-line {
    align-items: stretch;
    flex-direction: column;
  }

  .filter-grid,
  .summary-grid,
  .info-grid,
  .details-grid,
  .agency-block dl,
  .record-actions {
    grid-template-columns: 1fr;
  }

  .summary-card {
    min-height: 70px;
  }
}
</style>
